'use client';

import { useState, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import type { ManualCompetitor } from './CompetitorLocationsManager';

// ─── Types ────────────────────────────────────────────────────────────────────
interface SearchResult {
  place_id: string;
  name: string;
  city: string;
  state: string;
  lat: number;
  lng: number;
  vicinity: string;
  rating: number | null;
  reviewCount: number | null;
  businessStatus: string;
  threatLevel: number;
  estimatedSize: 'large' | 'medium' | 'small';
  marketPositioning: string;
  marketShareEstimate: string;
  aiNotes: string;
  // Enriched fields
  phone?: string;
  website?: string;
  formattedAddress?: string;
  businessHours?: string[];
  googleMapsUrl?: string;
  yearsInBusiness?: string;
  knownOwners?: string;
  specialties?: string;
  competitiveWeaknesses?: string;
}

interface CityCompetitorSearchProps {
  onAddToTracker: (competitor: ManualCompetitor) => void;
  trackedIds: Set<string>;
}

// ─── Config ───────────────────────────────────────────────────────────────────
const SIZE_CONFIG = {
  large:  { label: 'Large',  color: '#EF4444', bg: '#FEE2E2' },
  medium: { label: 'Medium', color: '#F59E0B', bg: '#FEF3C7' },
  small:  { label: 'Small',  color: '#6B7280', bg: '#F3F4F6' },
};

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

function getThreatColor(level: number): string {
  if (level >= 8) return '#EF4444';
  if (level >= 5) return '#F59E0B';
  return '#10B981';
}

function getThreatLabel(level: number): string {
  if (level >= 8) return 'High Threat';
  if (level >= 5) return 'Moderate';
  return 'Low Threat';
}

// ─── Result Card ──────────────────────────────────────────────────────────────
interface ResultCardProps {
  result: SearchResult;
  onAdd: (result: SearchResult) => void;
  isAdded: boolean;
  isAdding: boolean;
}

function ResultCard({ result, onAdd, isAdded, isAdding }: ResultCardProps) {
  const sizeCfg = SIZE_CONFIG[result.estimatedSize];
  const threatColor = getThreatColor(result.threatLevel);
  const threatLabel = getThreatLabel(result.threatLevel);

  return (
    <div
      className="rounded-xl overflow-hidden transition-all"
      style={{
        border: isAdded ? '1.5px solid #10B981' : '1px solid #E5E7EB',
        background: isAdded ? '#F0FDF4' : '#fff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      }}
    >
      {/* Card header */}
      <div className="flex items-start gap-3 p-4">
        {/* Threat level badge */}
        <div
          className="w-10 h-10 rounded-xl flex flex-col items-center justify-center flex-shrink-0"
          style={{ background: `${threatColor}18`, border: `1.5px solid ${threatColor}40` }}
        >
          <span className="text-sm font-black leading-none" style={{ color: threatColor }}>{result.threatLevel}</span>
          <span className="text-[8px] font-bold leading-none mt-0.5" style={{ color: threatColor }}>THREAT</span>
        </div>

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h4 className="text-sm font-bold truncate" style={{ color: '#1C2E5E' }}>{result.name}</h4>
              <p className="text-xs mt-0.5 truncate" style={{ color: '#6B7280' }}>{result.vicinity || `${result.city}, ${result.state}`}</p>
            </div>
            {/* Add button */}
            <button
              onClick={() => !isAdded && onAdd(result)}
              disabled={isAdded || isAdding}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold flex-shrink-0 transition-all hover:opacity-90 disabled:opacity-60"
              style={{
                background: isAdded ? '#10B981' : 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                color: '#fff',
              }}
            >
              {isAdding ? (
                <div className="w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
              ) : isAdded ? (
                <Icon name="CheckIcon" size={11} />
              ) : (
                <Icon name="PlusIcon" size={11} />
              )}
              {isAdded ? 'Added' : isAdding ? 'Adding...' : 'Add to Tracker'}
            </button>
          </div>

          {/* Badges row */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {/* Size */}
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: sizeCfg.bg, color: sizeCfg.color }}
            >
              {sizeCfg.label}
            </span>
            {/* Threat label */}
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: `${threatColor}18`, color: threatColor }}
            >
              {threatLabel}
            </span>
            {/* Rating */}
            {result.rating != null && (
              <span className="text-[10px] font-semibold" style={{ color: '#F59E0B' }}>
                ⭐ {result.rating} ({result.reviewCount?.toLocaleString() ?? 0} reviews)
              </span>
            )}
            {/* Business status */}
            {result.businessStatus === 'CLOSED_PERMANENTLY' && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#FEE2E2', color: '#EF4444' }}>
                Permanently Closed
              </span>
            )}
          </div>
        </div>
      </div>

      {/* AI enrichment row */}
      <div className="px-4 pb-3 grid grid-cols-3 gap-2">
        <div className="rounded-lg px-2.5 py-2" style={{ background: '#F8FAFF', border: '1px solid #E0E7FF' }}>
          <div className="text-[9px] font-bold uppercase tracking-wide mb-0.5" style={{ color: '#6366F1' }}>Market Share</div>
          <div className="text-xs font-black" style={{ color: '#1E1B4B' }}>{result.marketShareEstimate}</div>
        </div>
        <div className="col-span-2 rounded-lg px-2.5 py-2" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
          <div className="text-[9px] font-bold uppercase tracking-wide mb-0.5" style={{ color: '#9CA3AF' }}>Positioning</div>
          <div className="text-xs font-semibold truncate" style={{ color: '#374151' }}>{result.marketPositioning}</div>
        </div>
      </div>

      {/* Enriched details row */}
      {(result.phone || result.website || result.yearsInBusiness || result.knownOwners) && (
        <div className="px-4 pb-3 grid grid-cols-2 gap-2">
          {result.phone && (
            <div className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 flex-shrink-0" style={{ color: '#10B981' }}>
                <path fillRule="evenodd" d="M2 3.5A1.5 1.5 0 013.5 2h1.148a1.5 1.5 0 011.465 1.175l.716 3.223a1.5 1.5 0 01-1.052 1.767l-.933.267c-.41.117-.643.555-.48.95a11.542 11.542 0 006.254 6.254c.395.163.833-.07.95-.48l.267-.933a1.5 1.5 0 011.767-1.052l3.223.716A1.5 1.5 0 0118 15.352V16.5a1.5 1.5 0 01-1.5 1.5H15c-1.149 0-2.263-.15-3.326-.43A13.022 13.022 0 012.43 8.326 13.019 13.019 0 012 5V3.5z" clipRule="evenodd" />
              </svg>
              <span className="text-[10px] font-semibold truncate" style={{ color: '#065F46' }}>{result.phone}</span>
            </div>
          )}
          {result.website && (
            <div className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5" style={{ background: '#EEF2FF', border: '1px solid #C7D2FE' }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 flex-shrink-0" style={{ color: '#6366F1' }}>
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
              </svg>
              <a href={result.website} target="_blank" rel="noopener noreferrer" className="text-[10px] font-semibold truncate hover:underline" style={{ color: '#4338CA' }}>
                {result.website.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
              </a>
            </div>
          )}
          {result.yearsInBusiness && (
            <div className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 flex-shrink-0" style={{ color: '#D97706' }}>
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z" clipRule="evenodd" />
              </svg>
              <span className="text-[10px] font-semibold" style={{ color: '#92400E' }}>{result.yearsInBusiness}</span>
            </div>
          )}
          {result.knownOwners && result.knownOwners !== 'Not publicly listed' && (
            <div className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5" style={{ background: '#F5F3FF', border: '1px solid #DDD6FE' }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 flex-shrink-0" style={{ color: '#7C3AED' }}>
                <path d="M10 8a3 3 0 100-6 3 3 0 000 6zM3.465 14.493a1.23 1.23 0 00.41 1.412A9.957 9.957 0 0010 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 00-13.074.003z" />
              </svg>
              <span className="text-[10px] font-semibold truncate" style={{ color: '#5B21B6' }}>{result.knownOwners}</span>
            </div>
          )}
        </div>
      )}

      {/* Specialties + Weaknesses */}
      {(result.specialties || result.competitiveWeaknesses) && (
        <div className="px-4 pb-3 grid grid-cols-2 gap-2">
          {result.specialties && (
            <div className="rounded-lg px-2.5 py-1.5" style={{ background: '#F0F9FF', border: '1px solid #BAE6FD' }}>
              <div className="text-[9px] font-bold uppercase tracking-wide mb-0.5" style={{ color: '#0284C7' }}>Specialties</div>
              <div className="text-[10px]" style={{ color: '#0C4A6E' }}>{result.specialties}</div>
            </div>
          )}
          {result.competitiveWeaknesses && (
            <div className="rounded-lg px-2.5 py-1.5" style={{ background: '#FFF7ED', border: '1px solid #FED7AA' }}>
              <div className="text-[9px] font-bold uppercase tracking-wide mb-0.5" style={{ color: '#EA580C' }}>Weakness</div>
              <div className="text-[10px]" style={{ color: '#9A3412' }}>{result.competitiveWeaknesses}</div>
            </div>
          )}
        </div>
      )}

      {/* AI notes */}
      {result.aiNotes && (
        <div className="mx-4 mb-3 rounded-lg px-3 py-2 flex items-start gap-2" style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', border: '1px solid #DBEAFE' }}>
          <div className="w-4 h-4 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
            <Icon name="SparklesIcon" size={8} className="text-white" />
          </div>
          <p className="text-[10px] leading-relaxed" style={{ color: '#1E3A5F' }}>{result.aiNotes}</p>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const CityCompetitorSearch = ({ onAddToTracker, trackedIds }: CityCompetitorSearchProps) => {
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchedCity, setSearchedCity] = useState('');
  const [addingIds, setAddingIds] = useState<Set<string>>(new Set());
  const [localAdded, setLocalAdded] = useState<Set<string>>(new Set());
  const [filterSize, setFilterSize] = useState<'all' | 'large' | 'medium' | 'small'>('all');

  const handleSearch = useCallback(async () => {
    if (!city.trim() || !state.trim()) return;
    setLoading(true);
    setError('');
    setResults([]);
    setLocalAdded(new Set());
    setFilterSize('all');

    try {
      const res = await fetch('/api/expansion/competitor-search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ city: city.trim(), state: state.trim() }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error ?? 'Search failed');
      setResults(json.data ?? []);
      setSearchedCity(`${city.trim()}, ${state.trim().toUpperCase()}`);
    } catch (err: any) {
      setError(err.message ?? 'Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [city, state]);

  const handleAdd = useCallback(async (result: SearchResult) => {
    setAddingIds((prev) => new Set(prev).add(result.place_id));
    await new Promise((r) => setTimeout(r, 300));

    const competitor: ManualCompetitor = {
      id: `places-${result.place_id}`,
      name: result.name,
      city: result.city,
      state: result.state,
      lat: result.lat,
      lng: result.lng,
      size: result.estimatedSize,
      source: 'google_places',
      rating: result.rating ?? undefined,
      reviewCount: result.reviewCount ?? undefined,
      vicinity: result.formattedAddress ?? (result.vicinity || undefined),
      notes: result.aiNotes ? `[AI] ${result.aiNotes} | Positioning: ${result.marketPositioning} | Market Share: ${result.marketShareEstimate}` : undefined,
      marketShare: result.marketShareEstimate,
      // Enriched fields
      phone: result.phone,
      website: result.website,
      formattedAddress: result.formattedAddress,
      businessHours: result.businessHours,
      googleMapsUrl: result.googleMapsUrl,
      yearsInBusiness: result.yearsInBusiness,
      knownOwners: result.knownOwners,
      specialties: result.specialties,
      competitiveWeaknesses: result.competitiveWeaknesses,
      createdAt: new Date().toISOString(),
    };

    onAddToTracker(competitor);
    setLocalAdded((prev) => new Set(prev).add(result.place_id));
    setAddingIds((prev) => { const s = new Set(prev); s.delete(result.place_id); return s; });
  }, [onAddToTracker]);

  const filtered = results.filter((r) => filterSize === 'all' || r.estimatedSize === filterSize);

  const counts = {
    large: results.filter((r) => r.estimatedSize === 'large').length,
    medium: results.filter((r) => r.estimatedSize === 'medium').length,
    small: results.filter((r) => r.estimatedSize === 'small').length,
  };

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: '#fff', boxShadow: '0 2px 16px rgba(0,0,0,0.07)', border: '1px solid #E5E7EB' }}
    >
      {/* ── Header ── */}
      <div
        className="flex items-center justify-between px-6 py-4"
        style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(232,154,60,0.25)' }}>
            <Icon name="MagnifyingGlassIcon" size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white">City Competitor Search</h2>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Google Places + Gemini AI enrichment — find top roofing companies in any city
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg" style={{ background: 'rgba(232,154,60,0.15)', border: '1px solid rgba(232,154,60,0.3)' }}>
          <div className="w-1.5 h-1.5 rounded-full bg-[#E89A3C]" />
          <span className="text-[10px] font-bold text-[#E89A3C]">LIVE DATA</span>
        </div>
      </div>

      {/* ── Search Bar ── */}
      <div className="px-6 py-4" style={{ borderBottom: '1px solid #F3F4F6', background: '#FAFAFA' }}>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#374151' }}>City</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="e.g. Columbus, Dallas, Denver..."
              className="w-full px-3 py-2.5 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-blue-200"
              style={{ borderColor: '#E5E7EB', color: '#1C2E5E', background: '#fff' }}
            />
          </div>
          <div className="w-28">
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#374151' }}>State</label>
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-blue-200"
              style={{ borderColor: '#E5E7EB', color: '#1C2E5E', background: '#fff' }}
            >
              <option value="">--</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <button
            onClick={handleSearch}
            disabled={loading || !city.trim() || !state.trim()}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)', minWidth: 120 }}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Icon name="MagnifyingGlassIcon" size={15} />
                Search
              </>
            )}
          </button>
        </div>

        {/* Loading state description */}
        {loading && (
          <div className="mt-3 flex items-center gap-2 text-xs" style={{ color: '#6B7280' }}>
            <div className="w-3 h-3 rounded-full border border-[#E89A3C] border-t-transparent animate-spin" />
            <span>Searching Google Places for roofing companies, then enriching with Gemini AI...</span>
          </div>
        )}
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="mx-6 mt-4 flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
          <Icon name="ExclamationTriangleIcon" size={14} style={{ color: '#EF4444' }} />
          <span className="text-xs font-semibold" style={{ color: '#DC2626' }}>{error}</span>
        </div>
      )}

      {/* ── Results ── */}
      {results.length > 0 && (
        <>
          {/* Results header + filter */}
          <div className="px-6 py-3 flex items-center justify-between flex-wrap gap-3" style={{ borderBottom: '1px solid #F3F4F6', background: '#FAFAFA' }}>
            <div>
              <span className="text-sm font-bold" style={{ color: '#1C2E5E' }}>
                {results.length} competitors found in {searchedCity}
              </span>
              <span className="text-xs ml-2" style={{ color: '#9CA3AF' }}>sorted by threat level</span>
            </div>
            {/* Size filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Filter:</span>
              {(['all', 'large', 'medium', 'small'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterSize(s)}
                  className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
                  style={{
                    background: filterSize === s ? (s === 'all' ? '#1C2E5E' : SIZE_CONFIG[s as keyof typeof SIZE_CONFIG]?.bg ?? '#F3F4F6') : '#F3F4F6',
                    color: filterSize === s ? (s === 'all' ? '#fff' : SIZE_CONFIG[s as keyof typeof SIZE_CONFIG]?.color ?? '#374151') : '#6B7280',
                    border: `1px solid ${filterSize === s ? (s === 'all' ? '#1C2E5E' : SIZE_CONFIG[s as keyof typeof SIZE_CONFIG]?.color ?? '#E5E7EB') : '#E5E7EB'}`,
                  }}
                >
                  {s === 'all' ? `All (${results.length})` : `${SIZE_CONFIG[s as keyof typeof SIZE_CONFIG].label} (${counts[s as keyof typeof counts]})`}
                </button>
              ))}
            </div>
          </div>

          {/* Cards */}
          <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
            {filtered.map((result) => (
              <ResultCard
                key={result.place_id}
                result={result}
                onAdd={handleAdd}
                isAdded={localAdded.has(result.place_id) || trackedIds.has(`places-${result.place_id}`)}
                isAdding={addingIds.has(result.place_id)}
              />
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-8 text-sm" style={{ color: '#9CA3AF' }}>
                No {filterSize} competitors found in this search.
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 flex items-center gap-2" style={{ borderTop: '1px solid #F3F4F6', background: '#FAFAFA' }}>
            <div className="w-4 h-4 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
              <Icon name="SparklesIcon" size={8} className="text-white" />
            </div>
            <span className="text-[10px]" style={{ color: '#9CA3AF' }}>
              Threat levels, size estimates, and market positioning powered by Gemini AI based on Google Places data
            </span>
          </div>
        </>
      )}

      {/* ── Empty state ── */}
      {!loading && results.length === 0 && !error && (
        <div className="flex flex-col items-center justify-center py-14 px-6">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)', border: '1px solid #BFDBFE' }}
          >
            <Icon name="MagnifyingGlassIcon" size={24} style={{ color: '#1C2E5E' }} />
          </div>
          <h3 className="text-sm font-bold mb-1" style={{ color: '#1C2E5E' }}>Search Any City</h3>
          <p className="text-xs text-center max-w-xs" style={{ color: '#9CA3AF' }}>
            Enter a city and state to find top roofing companies via Google Places, enriched with Gemini AI threat analysis and market positioning.
          </p>
          <div className="mt-4 grid grid-cols-3 gap-2 w-full max-w-xs">
            {['Columbus, OH', 'Dallas, TX', 'Denver, CO'].map((example) => {
              const [c, s] = example.split(', ');
              return (
                <button
                  key={example}
                  onClick={() => { setCity(c); setState(s); }}
                  className="px-2 py-1.5 rounded-lg text-[10px] font-semibold text-center transition-all hover:opacity-80"
                  style={{ background: '#EFF6FF', color: '#1C2E5E', border: '1px solid #DBEAFE' }}
                >
                  {example}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CityCompetitorSearch;
